import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { ProtectedRoute } from './components/Auth/ProtectedRoute';
import { HELP_TOPICS } from './help/helpRegistry';
import './styles/globals.css';

const loadDashboard = () => import('./pages/Dashboard');
const loadSubastas = () => import('./pages/Subastas');
const loadDetalle = () => import('./pages/Detalle');
const loadRemanentes = () => import('./pages/Remanentes');
const loadRemanenteDetalle = () => import('./pages/RemanenteDetalle');
const loadRemanenteBeneficiario = () => import('./pages/RemanenteBeneficiario');
const loadConfig = () => import('./pages/Config');
const loadEquipo = () => import('./pages/Equipo');
const loadLogs = () => import('./pages/Logs');
const loadIngesta = () => import('./pages/Ingesta');
const loadIngestaL2Ficha = () => import('./pages/IngestaL2Ficha');
const loadUsers = () => import('./pages/Users');
const loadUserEdit = () => import('./pages/UserEdit');
const loadSetup = () => import('./pages/Setup');
const loadLanding = () => import('./pages/Landing');
const loadLogin = () => import('./pages/Login');
const loadRegister = () => import('./pages/Register');
const loadAyuda = () => import('./pages/Ayuda');
const loadAyudaTema = () => import('./pages/AyudaTema');

const Dashboard = lazy(loadDashboard);
const Subastas = lazy(loadSubastas);
const Detalle = lazy(loadDetalle);
const Remanentes = lazy(loadRemanentes);
const RemanenteDetalle = lazy(loadRemanenteDetalle);
const RemanenteBeneficiario = lazy(loadRemanenteBeneficiario);
const Config = lazy(loadConfig);
const Equipo = lazy(loadEquipo);
const Logs = lazy(loadLogs);
const Ingesta = lazy(loadIngesta);
const IngestaL2Ficha = lazy(loadIngestaL2Ficha);
const Users = lazy(loadUsers);
const UserEdit = lazy(loadUserEdit);
const Setup = lazy(loadSetup);
const Landing = lazy(loadLanding);
const Login = lazy(loadLogin);
const Register = lazy(loadRegister);
const Ayuda = lazy(loadAyuda);
const AyudaTema = lazy(loadAyudaTema);

const prefetchedModules = new Set();

function prefetchModules(modules) {
  for (const [key, loader] of modules) {
    if (prefetchedModules.has(key)) {
      continue;
    }

    prefetchedModules.add(key);
    loader().catch(() => {
      prefetchedModules.delete(key);
    });
  }
}

function schedulePrefetch(modules) {
  if (modules.length === 0) {
    return () => {};
  }

  if (typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function') {
    const callbackId = window.requestIdleCallback(() => prefetchModules(modules), { timeout: 1500 });
    return () => window.cancelIdleCallback(callbackId);
  }

  const timeoutId = window.setTimeout(() => prefetchModules(modules), 350);
  return () => window.clearTimeout(timeoutId);
}

function RoutePrefetch() {
  const location = useLocation();

  useEffect(() => {
    const pathname = location.pathname;
    const modules = [];
    const add = (key, loader) => modules.push([key, loader]);

    if (pathname === '/') {
      add('login', loadLogin);
      add('register', loadRegister);
    }

    if (pathname === '/login' || pathname.startsWith('/register/')) {
      add('dashboard', loadDashboard);
      add('subastas', loadSubastas);
    }

    if (pathname === '/dashboard') {
      add('subastas', loadSubastas);
      add('remanentes', loadRemanentes);
    }

    if (pathname === '/subastas') {
      add('detalle', loadDetalle);
      add('dashboard', loadDashboard);
    }

    if (pathname.startsWith('/subastas/')) {
      add('subastas', loadSubastas);
      add('remanentes', loadRemanentes);
    }

    if (pathname === '/remanentes') {
      add('remanenteDetalle', loadRemanenteDetalle);
      add('remanenteBeneficiario', loadRemanenteBeneficiario);
    }

    if (pathname.startsWith('/remanentes/beneficiario/')) {
      add('remanenteDetalle', loadRemanenteDetalle);
    }

    if (pathname.startsWith('/remanentes/')) {
      add('remanentes', loadRemanentes);
    }

    if (pathname === '/equipo' || pathname === '/config') {
      add('setup', loadSetup);
      add('logs', loadLogs);
    }

    if (pathname === '/ingesta') {
      add('ingestaL2Ficha', loadIngestaL2Ficha);
    }

    if (pathname === '/usuarios') {
      add('userEdit', loadUserEdit);
    }

    if (pathname === '/logs') {
      add('setup', loadSetup);
      add('users', loadUsers);
    }

    if (pathname === '/setup') {
      add('config', loadConfig);
      add('equipo', loadEquipo);
    }

    return schedulePrefetch(modules);
  }, [location.pathname]);

  return null;
}

function RouteLoader() {
  return (
    <div className="flex min-h-screen items-center justify-center text-sm text-slate-500">
      Cargando...
    </div>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <Router>
        <RoutePrefetch />
        <Suspense fallback={<RouteLoader />}>
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register/:token" element={<Register />} />

            {/* Protected Routes */}
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            } />
            <Route path="/subastas" element={
              <ProtectedRoute>
                <Subastas />
              </ProtectedRoute>
            } />
            <Route path="/subastas/:id" element={
              <ProtectedRoute>
                <Detalle />
              </ProtectedRoute>
            } />
            <Route path="/remanentes" element={
              <ProtectedRoute permiso="remanentes">
                <Remanentes />
              </ProtectedRoute>
            } />
            <Route path="/remanentes/beneficiario/:beneficiario" element={
              <ProtectedRoute permiso="remanentes">
                <RemanenteBeneficiario />
              </ProtectedRoute>
            } />
            <Route path="/remanentes/:id" element={
              <ProtectedRoute permiso="remanentes">
                <RemanenteDetalle />
              </ProtectedRoute>
            } />
            <Route path="/config" element={
              <ProtectedRoute roles={['admin', 'superadmin', 'boss']}>
                <Config />
              </ProtectedRoute>
            } />
            <Route path="/equipo" element={
              <ProtectedRoute roles={['admin', 'superadmin', 'boss']}>
                <Equipo />
              </ProtectedRoute>
            } />
            <Route path="/logs" element={
              <ProtectedRoute roles={['admin', 'superadmin']}>
                <Logs />
              </ProtectedRoute>
            } />
            <Route path="/ingesta" element={
              <ProtectedRoute roles={['admin', 'superadmin']}>
                <Ingesta />
              </ProtectedRoute>
            } />
            <Route path="/ingesta/l2/:idBoe" element={
              <ProtectedRoute roles={['admin', 'superadmin']}>
                <IngestaL2Ficha />
              </ProtectedRoute>
            } />
            <Route path="/usuarios" element={
              <ProtectedRoute roles={['admin', 'superadmin']}>
                <Users />
              </ProtectedRoute>
            } />
            <Route path="/usuarios/:id" element={
              <ProtectedRoute roles={['admin', 'superadmin']}>
                <UserEdit />
              </ProtectedRoute>
            } />
            <Route path="/setup" element={
              <ProtectedRoute roles={['admin', 'superadmin']}>
                <Setup />
              </ProtectedRoute>
            } />

            {/* Ayuda contextual. Las restricciones de cada tema viven en
                src/help/helpRegistry.js y deben mantenerse sincronizadas con
                las rutas de pantalla de arriba. */}
            <Route path="/ayuda" element={
              <ProtectedRoute>
                <Ayuda />
              </ProtectedRoute>
            } />
            {HELP_TOPICS.map((topic) => (
              <Route key={topic.id} path={`/ayuda/${topic.id}`} element={
                <ProtectedRoute roles={topic.restriccion.roles || []} permiso={topic.restriccion.permiso || null}>
                  <AyudaTema topic={topic} />
                </ProtectedRoute>
              } />
            ))}
            <Route path="/ayuda/*" element={<Navigate to="/ayuda" replace />} />

            {/* Fallback */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </Router>
    </AuthProvider>
  );
}
